import React, { useEffect, useState, useRef } from "react";
import { Form, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {toast, ToastContainer} from "react-toastify";
import Loader from "../../components/Loader";
import io from "socket.io-client";
import { crmHeaders } from "../../utils/crmApi";
import { canUseEmailSender, canEditEmailTemplates } from "../../utils/userAccess";
import RichTextToolbar, { RichTextEditor } from "../../components/RichTextToolbar";

const EMAIL_SERVER_URL = process.env.REACT_APP_EMAIL_SERVER_URL || process.env.REACT_APP_BASE_URL;
const socket = io(EMAIL_SERVER_URL, {
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 8000,
    timeout: 60000,
});
const sessionId = Math.random().toString(36).substr(2, 9);
const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function SendEmails(){
    const API_URL = BASE_URL+"/authenticateemail";
    const navigate = useNavigate();

    //getting countries
    const GET_EMAIL_URL = BASE_URL + "/getemails";

    const [emails, setEmail] = useState([]);
    const [response, setResponse] = useState(null);

    //get emails
    useEffect(() => {
        axios.get(GET_EMAIL_URL, { headers: crmHeaders() })
        .then(
            res => {
                setEmail(res.data);
                setLoading(false);
            }
        )
        .catch(
            error => {
                console.log("There was an error", error)
            }
        )
    }, [])

    //getting countries
    const GET_COUNTRY_URL = BASE_URL+"/getcountries";

    const [countries, setCountry] = useState([]);
    useEffect(() => {
        axios.get(GET_COUNTRY_URL, { headers: crmHeaders() })
        .then(
            res => {
                setCountry(res.data);
                setLoading(false);
            }
        )
        .catch(
            error => {
                console.log("There was an error", error)
            }
        )
    }, [])

    //getting email template
    const TEMPLATE_URL = BASE_URL+"/gettemplates";

    const [templates, setTemplates] = useState([]);

    useEffect(() => {
        axios.get(TEMPLATE_URL, { headers: crmHeaders() })
        .then(
            res => {
                setTemplates(res.data);
                setLoading(false);
            }
        )
        .catch(
            error => {
                console.log("There was an error", error)
            }
        )
    }, [])

    const [getcountry, setGetCountry] = useState(null);
    const [getEmail, setGetEmail] = useState(null);
    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = new FormData(e.target);
        const email = formData.get("email");
        const country = formData.get("country");
        setGetCountry(formData.get("country"));
        setGetEmail(formData.get("email"));
        
        if(!email || !country){
            toast.error("Email and country are required!");
        }
        else{
            const data = {
                Email: email,
            }
            axios.post(API_URL, data, { headers: crmHeaders() })
            .then(
                res => {
                    // navigate("/emails/email-templates");
                    toast.success("Authenticated Successfully");

                    const filteredTemplates = templates.filter(template => template.Country === country);
                    if(filteredTemplates.length == 0){
                        toast.error(`There are no templates for ${country}`)
                    }
                    else{
                        setHostName(res.data.hostName);
                        setPort(res.data.port)
                        setResponse(res.data);
                        setActiveTemplateId(filteredTemplates[0].Id);
                        setSendMode("single");
                    }
                }
            )
            .catch(
                error => {
                    if(error.response.data.message == "Invalid Email or Password"){
                        toast.error("Invalid Email or Password!");
                    }
                    console.error(error);
                }
            )
        }
    }

    //emails sending code...
    const [hostName, setHostName] = useState(null);
    const [port, setPort] = useState(null);
    const [file, setFile] = useState(null);
    const [subject, setSubject] = useState("");
    const [progress, setProgress] = useState(0);
    const [totalEmails, setTotalEmails] = useState(0);
    const [sentEmails, setSentEmails] = useState([]);
    const [failedEmails, setFailedEmails] = useState([]);
    const [businessCard, setBusinessCard] = useState(null);
    const [emailSendConfig, setEmailSendConfig] = useState(null);
    const [lastProxy, setLastProxy] = useState(null);

    useEffect(() => {
        if (!response) return;
        axios
            .get(`${BASE_URL}/email-send-config`, { headers: crmHeaders() })
            .then((res) => setEmailSendConfig(res.data || null))
            .catch(() => setEmailSendConfig(null));
    }, [response]);

    useEffect(() => {
        if (!sessionId) return;

        const register = () => socket.emit("register", sessionId);

        socket.on("connect", register);
        register();

        socket.on("progress", ({ current, total, lastProxy: proxyLabel }) => {
            setProgress(current);
            setTotalEmails(total);
            setLoading(true);
            if (proxyLabel) setLastProxy(proxyLabel);
        });

        socket.on("emailResult", ({ success, failed }) => {
            setSentEmails(success);
            setFailedEmails(failed);
            setLoading(false);
        });

        return () => {
            socket.off("connect", register);
            socket.off("progress");
            socket.off("emailResult");
        };
    }, [sessionId]);        
    
    const startEmailSending = async () => {
        setIsStopped(false);
        setLoading(true);
        setProgress(0);
        setTotalEmails(0);
        setSentEmails([]);
        setFailedEmails([]);
        
        const data = new FormData();
        data.append("country", getcountry);
        data.append("csvFile", file);
        if (businessCard) data.append("businessCard", businessCard);
        data.append("email", getEmail);
        data.append("subject", subject);
        data.append("sessionId", sessionId);
    
        try {
            await axios.post(`${BASE_URL}/proxy-runemails`, data, {
                headers: { ...crmHeaders(), "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
        } catch (error) {
            console.error("Error uploading file:", error);
            toast.error(error.response?.data?.error || "Failed to start email sending");
            setLoading(false);
        }
    }; 

    const handleUpload = async () => {
        if (!file) {
            alert("Please upload a CSV file.");
            return;
        }
        if (isStopped) {
            alert("Session was stopped. Click Run again to start a new send.");
            return;
        }
    
        if (!scheduleDateTime) {
            startEmailSending();
            return;
        }
    
        const now = new Date();
        const scheduledTime = new Date(scheduleDateTime);
    
        if (isNaN(scheduledTime.getTime())) {
            alert("Invalid scheduled date.");
            return;
        }
    
        if (scheduledTime > now) {
            const delay = scheduledTime - now;
            toast.info(`Scheduled to start at ${scheduledTime.toLocaleString()}`);
            setTimeout(() => {
                startEmailSending();
            }, delay);
        } else {
            startEmailSending();
        }
    };   

    const [isStopped, setIsStopped] = useState(false);

    const handleStopEmails = () => {
        setIsStopped(true);
        socket.emit("stopEmails", sessionId); // Notify backend
        toast.info("Email sending stopped for this session.");
    };
    
    const [scheduleDateTime, setScheduleDateTime] = useState("");
    const [sendMode, setSendMode] = useState("bulk");
    const [singleTo, setSingleTo] = useState("");
    const [singleSubject, setSingleSubject] = useState("");
    const [singleSending, setSingleSending] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState(null);
    const [editTemplateHtml, setEditTemplateHtml] = useState("");
    const [activeTemplateId, setActiveTemplateId] = useState(null);
    const singleEditorRef = useRef(null);
    const canEditTemplates = canEditEmailTemplates();

    const countryTemplates = templates.filter((template) => template.Country === getcountry);
    const activeTemplate =
        countryTemplates.find((t) => t.Id === activeTemplateId) || countryTemplates[0] || null;

    const loadTemplateIntoEditor = (html) => {
        if (singleEditorRef.current) {
            singleEditorRef.current.innerHTML = html || "";
        }
    };

    useEffect(() => {
        if (!response || sendMode !== "single" || !activeTemplate) return;
        const html = activeTemplate.Template;
        loadTemplateIntoEditor(html);
        const timer = setTimeout(() => loadTemplateIntoEditor(html), 0);
        return () => clearTimeout(timer);
    }, [response, sendMode, activeTemplate?.Id, activeTemplate?.Template]);

    const sendSingleEmail = async () => {
        if (!getEmail || !singleTo.trim()) {
            toast.error("Recipient email is required");
            return;
        }
        const body = singleEditorRef.current?.innerHTML?.trim() || "";
        if (!body) {
            toast.error("Message body is required");
            return;
        }
        setSingleSending(true);
        try {
            const formData = new FormData();
            formData.append("Email", getEmail);
            formData.append("to", singleTo.trim());
            formData.append("subject", singleSubject.trim() || "(No Subject)");
            formData.append("body", body);
            const res = await axios.post(`${BASE_URL}/sendemail`, formData, {
                headers: { ...crmHeaders(), "Content-Type": "multipart/form-data" },
            });
            const route = res.data?.proxyRoute;
            const originIp = res.data?.originIp;
            toast.success(
                route === "us-vps" || emailSendConfig?.remoteWorker
                    ? `Email sent via US server${originIp ? ` (${originIp})` : ""}`
                    : route && route !== "direct"
                      ? `Email sent — IP changed via ${route}`
                      : route === "direct"
                        ? "Email sent (direct — auto IP routes busy, email delivered)"
                        : "Email sent successfully"
            );
            setSingleTo("");
            setSingleSubject("");
            loadTemplateIntoEditor(activeTemplate?.Template || "");
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to send email");
        } finally {
            setSingleSending(false);
        }
    };

    const saveTemplateEdit = async (template) => {
        if (!editTemplateHtml.trim()) {
            toast.error("Template cannot be empty");
            return;
        }
        try {
            await axios.put(
                `${BASE_URL}/updatetemplate`,
                { Id: template.Id, Country: template.Country, Template: editTemplateHtml },
                { headers: crmHeaders() }
            );
            toast.success("Template updated");
            setEditingTemplateId(null);
            const res = await axios.get(TEMPLATE_URL, { headers: crmHeaders() });
            setTemplates(res.data);
            if (template.Id === activeTemplateId) {
                loadTemplateIntoEditor(editTemplateHtml);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update template");
        }
    };

    const [loading, setLoading] = useState(true);
    if (!canUseEmailSender()) {
        return (
            <div className="container p-4">
                <h3>Access Denied</h3>
                <p className="text-muted">Email Sender access is not enabled for your account.</p>
            </div>
        );
    }
    if (loading) return <Loader />;
    return(
        <div className="container">
            <ToastContainer />
            <div className="crm-page-header">
                <div>
                    <h1 className="crm-page-title">Email Sender</h1>
                    <p className="crm-page-subtitle">Bulk campaigns or single emails with rich formatting</p>
                </div>
            </div>
            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-sm-6">
                        <select className="form-control" name="email" required disabled={response}>
                            <option disabled selected>Select Email</option>
                            {emails
                            .map(email => (
                                <option value={email.Email}>{email.Email}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-sm-6">
                        <select className="form-control" name="country" required disabled={response}>
                            <option disabled selected>Select Template</option>
                            {countries
                            .filter(country => country.Status === "Active")
                            .map(country => (
                                <option value={country.Country}>{country.Country}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <br />
                <div className="row">
                    <div className="col-sm-6">
                        <button className="btn btn-primary" disabled={response}>Connect Email</button>
                    </div>
                </div>
            </form>
            <br />
            {response && (
                <div className="row">
                    <div className="crm-send-mode-tabs">
                        <button
                            type="button"
                            className={sendMode === "bulk" ? "active" : ""}
                            onClick={() => setSendMode("bulk")}
                        >
                            Bulk Send (CSV)
                        </button>
                        <button
                            type="button"
                            className={sendMode === "single" ? "active" : ""}
                            onClick={() => setSendMode("single")}
                        >
                            Single Email
                        </button>
                    </div>

                    <h3>Email templates for {getcountry}</h3>
                    <div className="d-flex flex-wrap gap-3 mb-4">
                    {countryTemplates.map((template) => (
                        <div
                            className={`card ${activeTemplateId === template.Id ? "border-primary" : ""}`}
                            style={{ width: "22rem" }}
                            key={template.Id}
                        >
                            <div className="card-body">
                                {editingTemplateId === template.Id ? (
                                    <>
                                        <textarea
                                            className="form-control"
                                            rows={8}
                                            value={editTemplateHtml}
                                            onChange={(e) => setEditTemplateHtml(e.target.value)}
                                        />
                                        <div className="mt-2 d-flex gap-2">
                                            <button type="button" className="btn btn-sm btn-primary" onClick={() => saveTemplateEdit(template)}>
                                                Save
                                            </button>
                                            <button type="button" className="btn btn-sm btn-secondary" onClick={() => setEditingTemplateId(null)}>
                                                Cancel
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div dangerouslySetInnerHTML={{ __html: template.Template }} />
                                        <div className="mt-2 d-flex gap-2 flex-wrap">
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${activeTemplateId === template.Id ? "btn-primary" : "btn-outline-primary"}`}
                                                onClick={() => {
                                                    setActiveTemplateId(template.Id);
                                                    setSendMode("single");
                                                    loadTemplateIntoEditor(template.Template);
                                                }}
                                            >
                                                {activeTemplateId === template.Id ? "In email body" : "Use this template"}
                                            </button>
                                            {canEditTemplates && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => {
                                                        setEditingTemplateId(template.Id);
                                                        setEditTemplateHtml(template.Template);
                                                    }}
                                                >
                                                    Edit template
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                    </div>

                    {sendMode === "single" ? (
                        <div className="crm-user-form-card mb-4">
                            <div className="crm-user-form-card-header">
                                <i className="bi bi-envelope-paper"></i>
                                <div>
                                    <h5>Send Single Email</h5>
                                    <span>From {getEmail}</span>
                                </div>
                            </div>
                            <div className="crm-user-form-card-body">
                                {emailSendConfig?.remoteWorker ? (
                                    <div className="alert alert-success py-2 mb-3">
                                        <i className="bi bi-globe-americas me-2"></i>
                                        <strong>US Remote Server ON</strong> — email US VPS se jayegi
                                        {emailSendConfig.workerOrigin?.ip && (
                                            <span className="d-block small mt-1">
                                                Outbound IP: {emailSendConfig.workerOrigin.ip} (
                                                {emailSendConfig.workerOrigin.country || "US"})
                                            </span>
                                        )}
                                    </div>
                                ) : emailSendConfig?.originMode === "remote" ? (
                                    <div className="alert alert-warning py-2 mb-3">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        <strong>US VPS setup pending</strong> — <code>server/.env</code> mein{" "}
                                        <code>EMAIL_WORKER_URL=http://APNA_US_VPS_IP:5001</code> set karein,
                                        phir server restart
                                    </div>
                                ) : emailSendConfig?.autoIp || emailSendConfig?.proxy?.mode === "auto-ip" ? (
                                    <div className="alert alert-success py-2 mb-3">
                                        <i className="bi bi-arrow-repeat me-2"></i>
                                        <strong>Auto IP Change ON</strong> — har email alag US IP se jayegi
                                        ({emailSendConfig.proxy?.proxyCount || 0} route
                                        {(emailSendConfig.proxy?.proxyCount || 0) !== 1 ? "s" : ""} ready, no VPS/VPN)
                                    </div>
                                ) : emailSendConfig?.proxy?.enabled ? (
                                    <div className="alert alert-success py-2 mb-3">
                                        <i className="bi bi-shield-lock me-2"></i>
                                        <strong>USA IP Rotation ON</strong> — email US route se jayegi
                                    </div>
                                ) : emailSendConfig?.proxy?.mode === "auto-ip" || emailSendConfig?.originMode === "local" ? (
                                    <div className="alert alert-warning py-2 mb-3">
                                        <i className="bi bi-hourglass-split me-2"></i>
                                        Auto IP routes load ho rahe hain — 30 sec wait karein ya email-server restart
                                    </div>
                                ) : null}
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">To</label>
                                        <input
                                            className="form-control"
                                            type="email"
                                            value={singleTo}
                                            onChange={(e) => setSingleTo(e.target.value)}
                                            placeholder="recipient@example.com"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Subject</label>
                                        <input
                                            className="form-control"
                                            value={singleSubject}
                                            onChange={(e) => setSingleSubject(e.target.value)}
                                            placeholder="Email subject"
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">
                                            Message
                                            {activeTemplate && (
                                                <small className="text-muted ms-2">
                                                    ({getcountry} template loaded)
                                                </small>
                                            )}
                                        </label>
                                        <RichTextToolbar editorRef={singleEditorRef} />
                                        <RichTextEditor editorRef={singleEditorRef} placeholder="Write your email..." />
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-primary mt-3"
                                    onClick={sendSingleEmail}
                                    disabled={singleSending}
                                >
                                    {singleSending ? "Sending..." : "Send Email"}
                                </button>
                            </div>
                        </div>
                    ) : (
                    <div>
                                <h2>Upload Data Here</h2>
                                {emailSendConfig?.remoteWorker ? (
                                    <div className="alert alert-success py-2 mb-3">
                                        <i className="bi bi-globe-americas me-2"></i>
                                        <strong>US Remote Server ON</strong> — bulk email US VPS se jayegi
                                        {emailSendConfig.workerOrigin?.ip && (
                                            <span className="d-block small mt-1">
                                                Outbound IP: {emailSendConfig.workerOrigin.ip}
                                            </span>
                                        )}
                                    </div>
                                ) : emailSendConfig?.originMode === "remote" ? (
                                    <div className="alert alert-warning py-2 mb-3">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        <strong>US VPS setup pending</strong> — <code>server/.env</code> mein{" "}
                                        <code>EMAIL_WORKER_URL=http://APNA_US_VPS_IP:5001</code> set karein,
                                        phir server restart
                                    </div>
                                ) : emailSendConfig?.autoIp || emailSendConfig?.proxy?.mode === "auto-ip" ? (
                                    <div className="alert alert-success py-2 mb-3">
                                        <i className="bi bi-arrow-repeat me-2"></i>
                                        <strong>Auto IP Change ON</strong> — bulk mein har email alag US IP
                                        ({emailSendConfig.proxy?.proxyCount || 0} routes)
                                        {lastProxy && loading && (
                                            <span className="d-block small mt-1 text-muted">
                                                Current: {lastProxy}
                                            </span>
                                        )}
                                    </div>
                                ) : emailSendConfig?.proxy?.enabled ? (
                                    <div className="alert alert-success py-2 mb-3">
                                        <i className="bi bi-shield-lock me-2"></i>
                                        <strong>USA IP Rotation ON</strong> — har email alag route (
                                        {emailSendConfig.proxy.proxyCount} active)
                                        {lastProxy && loading && (
                                            <span className="d-block small mt-1 text-muted">
                                                Current route: {lastProxy}
                                            </span>
                                        )}
                                    </div>
                                ) : emailSendConfig?.proxy?.mode === "auto-ip" || emailSendConfig?.originMode === "local" ? (
                                    <div className="alert alert-warning py-2 mb-3">
                                        <i className="bi bi-hourglass-split me-2"></i>
                                        Auto IP routes load ho rahe hain — 30 sec wait ya email-server restart
                                    </div>
                                ) : (
                                    <p className="text-muted small mb-2">
                                        <i className="bi bi-info-circle me-1"></i>
                                        US IP ke liye free US VPS worker setup karein (proxy ki zaroorat nahi)
                                    </p>
                                )}
                                <p><i>Note: CSV must have maximum 2000 rows and column sequence like this <b>"email | clientName | businessName | phoneNumber"</b> with exact spellings.</i></p>

                                <div className="row">
                                    <div className="col-sm-6">
                                    <label>CSV File:</label>
                                        <input
                                            className="form-control"
                                            type="file"
                                            accept=".csv"
                                            onChange={(e) => setFile(e.target.files[0])}
                                        />
                                    </div>
                                    <div className="col-sm-6">
                                        <label>Business Card (Image URL):</label>
                                        <input
                                            className="form-control"
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => setBusinessCard(e.target.files[0])}
                                        />
                                    </div>
                                    <div className="col-sm-6 mt-3">
                                        <input
                                            className="form-control"
                                            type="text"
                                            placeholder="Enter Subject For The Emails"
                                            value={subject}
                                            onChange={(e) => setSubject(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <br />
                                <div className="col-sm-6 mt-3">
                                    <label>Schedule Email Run (optional):</label>
                                    <input
                                        className="form-control"
                                        type="datetime-local"
                                        value={scheduleDateTime}
                                        onChange={(e) => setScheduleDateTime(e.target.value)}
                                    />
                                </div>
                                <br />
                                <button className="btn btn-primary" onClick={handleUpload} disabled={loading}>
                                    {loading ? "Sending..." : "Run All Emails"}
                                </button>
                                <button
                                    className="btn btn-danger ms-2"
                                    onClick={handleStopEmails}
                                    disabled={isStopped || totalEmails === 0}
                                >
                                    Stop Emails
                                </button>

                                {/* Progress Bar */}
                                {totalEmails > 0 && (
                                    <h3>Progress: {progress}/{totalEmails} ({Math.round((progress / totalEmails) * 100)}%)</h3>
                                )}

                                {/* Successfully Sent Emails */}
                                {sentEmails.length > 0 && (
                                    <div>
                                        <h4>✅ Successfully Sent Emails ({sentEmails.length})</h4>
                                        <ul className="emails-list">
                                            {sentEmails.map((email, index) => (
                                                <li key={email || index}>{email}</li> // email ko key banana better hai
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Failed Emails */}
                                {failedEmails.length > 0 && (
                                    <div className="emails-list">
                                        <h4>❌ Failed Emails ({failedEmails.length})</h4>
                                        <ul>
                                            {failedEmails.map((email, index) => (
                                                <li key={email || index}>{email}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                    )}
                </div>
            )}
        </div>
    );
}